import { Mapper } from '../../../@harmony';
import { ServiceProvider } from '../entities/service-provider.enum';
import {
  CredentialEntity,
  DropboxCredential,
  GoogleCredential,
  MicrosoftCredential,
  ZohoCredential,
} from '../entities/credential.entity';
import { ServiceAuthType } from '../entities/service-auth-type.enum';

export class DropboxCredentialToCredentialEntityMapper
  implements Mapper<Partial<DropboxCredential>, CredentialEntity>
{
  constructor(private readonly now: number) {}

  public map(from: Partial<DropboxCredential>): CredentialEntity {
    const cred = this.getCredential(from);

    return {
      authType: ServiceAuthType.Dropbox,
      provider: ServiceProvider.Dropbox,
      accessToken: cred.access_token,
      expiresAt: this.now + cred.expires_in * 1000,
      refreshToken: cred.refresh_token,
      scopes: cred.scope.toLowerCase().split(' '),
      metadata: {},
    };
  }

  private getCredential(cred: Partial<DropboxCredential>): DropboxCredential {
    if (this.isCredential(cred)) {
      return cred;
    }

    throw new Error('Invalid Dropbox credential');
  }

  private isCredential(creds: object): creds is DropboxCredential {
    return (
      'access_token' in creds &&
      typeof creds.access_token === 'string' &&
      'expires_in' in creds &&
      typeof creds.expires_in === 'number' &&
      'refresh_token' in creds &&
      typeof creds.refresh_token === 'string' &&
      'scope' in creds &&
      typeof creds.scope === 'string'
    );
  }
}

export class GoogleCredentialToCredentialEntityMapper
  implements Mapper<Partial<GoogleCredential>, CredentialEntity>
{
  public map(from: Partial<GoogleCredential>): CredentialEntity {
    const cred = this.getCredential(from);

    return {
      authType: ServiceAuthType.Google,
      provider: ServiceProvider.Google,
      accessToken: cred.access_token,
      expiresAt: cred.expiry_date,
      refreshToken: cred.refresh_token,
      scopes: cred.scope.toLowerCase().split(' '),
      metadata: {},
    };
  }

  private getCredential(creds: Partial<GoogleCredential>): GoogleCredential {
    if (this.isCredential(creds)) {
      return creds;
    }

    throw new Error('Invalid Google credential');
  }

  private isCredential(creds: Partial<GoogleCredential>): creds is GoogleCredential {
    return (
      'access_token' in creds &&
      typeof creds.access_token === 'string' &&
      'expiry_date' in creds &&
      typeof creds.expiry_date === 'number' &&
      'refresh_token' in creds &&
      typeof creds.refresh_token === 'string' &&
      'scope' in creds &&
      typeof creds.scope === 'string'
    );
  }
}

export class MicrosoftCredentialToCredentialEntityMapper
  implements Mapper<Partial<MicrosoftCredential>, CredentialEntity>
{
  constructor(private readonly now: number) {}

  public map(from: Partial<MicrosoftCredential>): CredentialEntity {
    const cred = this.getCredential(from);
    const scopes = cred.scope.toLowerCase().split(' ');

    return {
      authType: scopes.includes('sites.selected')
        ? ServiceAuthType.MicrosoftTeamSites
        : ServiceAuthType.MicrosoftTeamFull,
      provider: ServiceProvider.Microsoft,
      accessToken: cred.access_token,
      expiresAt: this.now + cred.expires_in * 1000,
      refreshToken: cred.refresh_token,
      scopes,
      metadata: {},
    };
  }

  private getCredential(creds: Partial<MicrosoftCredential>): MicrosoftCredential {
    if (this.isCredential(creds)) {
      return creds;
    }

    throw new Error('Invalid Microsoft credential');
  }

  private isCredential(creds: Partial<MicrosoftCredential>): creds is MicrosoftCredential {
    return (
      'access_token' in creds &&
      typeof creds.access_token === 'string' &&
      'expires_in' in creds &&
      typeof creds.expires_in === 'number' &&
      'refresh_token' in creds &&
      typeof creds.refresh_token === 'string' &&
      'scope' in creds &&
      typeof creds.scope === 'string'
    );
  }
}

export class ZohoCredentialToCredentialEntityMapper
  implements Mapper<Partial<ZohoCredential>, CredentialEntity>
{
  constructor(private readonly now: number) {}

  public map(from: Partial<ZohoCredential>): CredentialEntity {
    const cred = this.getCredential(from);
    const metadata: Record<string, string> = { api_domain: cred.api_domain };
    if (cred.accounts_server) {
      metadata['accounts_server'] = cred.accounts_server;
    }

    return {
      authType: ServiceAuthType.Zoho,
      provider: ServiceProvider.Zoho,
      accessToken: cred.access_token,
      expiresAt: this.now + cred.expires_in * 1000,
      refreshToken: cred.refresh_token ?? '',
      scopes: cred.scope.split(/[, ]+/),
      metadata,
    };
  }

  private getCredential(cred: Partial<ZohoCredential>): ZohoCredential {
    if (this.isCredential(cred)) {
      return cred;
    }

    throw new Error('Invalid Zoho credential');
  }

  private isCredential(creds: Partial<ZohoCredential>): creds is ZohoCredential {
    // accounts_server is omitted by the implicit/client-based flow; only api_domain is required.
    return (
      'access_token' in creds &&
      typeof creds.access_token === 'string' &&
      'expires_in' in creds &&
      typeof creds.expires_in === 'number' &&
      'scope' in creds &&
      typeof creds.scope === 'string' &&
      'api_domain' in creds &&
      typeof creds.api_domain === 'string'
    );
  }
}
